/**
 * Share Page - Manage data sharing invitations
 *
 * Allows diabetic users to create share links for monitors and clinicians.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  relationshipService,
  type Invitation,
  type Monitor,
  type ClinicSummary,
  type ClinicShare,
} from "../services/relationships";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/checkbox/checkbox.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "../components/app-shell";

@customElement("share-page")
export class SharePage extends LitElement {
  @state() private invitations: Invitation[] = [];
  @state() private monitors: Monitor[] = [];
  @state() private availableClinics: ClinicSummary[] = [];
  @state() private clinicShares: ClinicShare[] = [];
  @state() private isLoading = true;
  @state() private error = "";
  @state() private showCreateDialog = false;
  @state() private showClinicDialog = false;
  @state() private newInviteRoles: Set<string> = new Set(["monitor"]);
  @state() private createdInviteUrl = "";
  @state() private isCreating = false;

  static styles = css`
    :host {
      display: block;
      padding: 1rem;
      max-width: 800px;
      margin: 0 auto;
    }

    h1 {
      margin: 0 0 1.5rem 0;
      font-size: 1.5rem;
      color: #333;
    }

    h2 {
      margin: 1.5rem 0 1rem 0;
      font-size: 1.125rem;
      color: #555;
    }

    .section {
      margin-bottom: 2rem;
    }

    .create-button {
      margin-bottom: 1rem;
    }

    .invitations-list,
    .monitors-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .invitation-card,
    .monitor-card {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .invitation-info,
    .monitor-info {
      flex: 1;
    }

    .invite-code {
      font-family: monospace;
      background: #e9ecef;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.875rem;
    }

    .invite-meta {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }

    .roles {
      display: flex;
      gap: 0.25rem;
      margin-top: 0.5rem;
    }

    .invite-actions {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }

    .empty-state {
      text-align: center;
      padding: 2rem;
      color: #6c757d;
    }

    .dialog-content {
      padding: 1rem 0;
    }

    .role-checkboxes {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin: 1rem 0;
    }

    .role-option {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .role-description {
      font-size: 0.75rem;
      color: #6c757d;
      margin-left: 1.5rem;
    }

    .invite-url-box {
      background: #e9ecef;
      padding: 1rem;
      border-radius: 0.5rem;
      margin-top: 1rem;
      word-break: break-all;
    }

    .invite-url-label {
      font-size: 0.75rem;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }

    .invite-url {
      font-family: monospace;
      font-size: 0.875rem;
    }

    .copy-button {
      margin-top: 0.5rem;
    }

    .status-badge {
      margin-left: 0.5rem;
    }

    .monitor-name {
      font-weight: 500;
    }

    .monitor-email {
      font-size: 0.875rem;
      color: #6c757d;
    }

    .monitor-type {
      margin-top: 0.25rem;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  private async loadData() {
    this.isLoading = true;
    this.error = "";

    try {
      const [invitations, monitors, availableClinics, clinicShares] =
        await Promise.all([
          relationshipService.getMyInvitations(),
          relationshipService.getMyMonitors(),
          relationshipService.getAvailableClinics(),
          relationshipService.getMyClinicShares(),
        ]);
      this.invitations = invitations;
      this.monitors = monitors;
      this.availableClinics = availableClinics;
      this.clinicShares = clinicShares;
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load data";
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    if (this.isLoading) {
      return html`
        <app-shell pageTitle="Share">
          <div class="spinner-container">
            <sl-spinner style="font-size: 2rem;"></sl-spinner>
          </div>
        </app-shell>
      `;
    }

    return html`
      <app-shell pageTitle="Share">
        <h1>Share My Data</h1>

        ${this.error
          ? html`<sl-alert variant="danger" open>${this.error}</sl-alert>`
          : ""}

        <div class="section">
          <sl-button
            class="create-button"
            variant="primary"
            @click=${() => (this.showCreateDialog = true)}
          >
            <sl-icon slot="prefix" name="plus-circle"></sl-icon>
            Create Share Link
          </sl-button>

          <h2>Active Invitations</h2>
          ${this.invitations.length === 0
            ? html`<div class="empty-state">No active invitations</div>`
            : html`
                <div class="invitations-list">
                  ${this.invitations.map((inv) => this.renderInvitation(inv))}
                </div>
              `}
        </div>

        <div class="section">
          <h2>People Monitoring Me</h2>
          ${this.monitors.length === 0
            ? html`<div class="empty-state">
                No one is monitoring your data yet
              </div>`
            : html`
                <div class="monitors-list">
                  ${this.monitors.map((m) => this.renderMonitor(m))}
                </div>
              `}
        </div>

        <div class="section">
          <sl-button
            class="create-button"
            variant="primary"
            @click=${() => (this.showClinicDialog = true)}
          >
            <sl-icon slot="prefix" name="hospital"></sl-icon>
            Add Clinic
          </sl-button>

          <h2>Clinics With Access</h2>
          ${this.clinicShares.length === 0
            ? html`<div class="empty-state">
                No clinics have access to your data yet
              </div>`
            : html`
                <div class="monitors-list">
                  ${this.clinicShares.map((c) => this.renderClinicShare(c))}
                </div>
              `}
        </div>

        ${this.renderCreateDialog()} ${this.renderClinicDialog()}
      </app-shell>
    `;
  }

  private renderClinicShare(share: ClinicShare) {
    return html`
      <div class="monitor-card">
        <div class="monitor-info">
          <div class="monitor-name">${share.clinicName}</div>
          <div class="monitor-email">
            Shared ${new Date(share.sharedAt).toLocaleDateString()}
          </div>
          <div class="monitor-type">
            <sl-badge variant="neutral">
              <sl-icon name="hospital" style="margin-right: 0.25rem;"></sl-icon>
              Clinic
            </sl-badge>
          </div>
        </div>
        <sl-button
          size="small"
          variant="danger"
          @click=${() => this.revokeClinicShare(share.clinicId)}
        >
          <sl-icon slot="prefix" name="x-circle"></sl-icon>
          Revoke Access
        </sl-button>
      </div>
    `;
  }

  private renderInvitation(inv: Invitation) {
    const isActive = !inv.isExpired && !inv.isFullyUsed;
    const expiresDate = new Date(inv.expiresAt);
    const isExpiringSoon =
      isActive && expiresDate.getTime() - Date.now() < 24 * 60 * 60 * 1000;

    return html`
      <div class="invitation-card">
        <div class="invitation-info">
          <span class="invite-code">${inv.inviteCode}</span>
          ${inv.isExpired
            ? html`<sl-badge class="status-badge" variant="danger"
                >Expired</sl-badge
              >`
            : inv.isFullyUsed
              ? html`<sl-badge class="status-badge" variant="neutral"
                  >Used</sl-badge
                >`
              : isExpiringSoon
                ? html`<sl-badge class="status-badge" variant="warning"
                    >Expiring Soon</sl-badge
                  >`
                : html`<sl-badge class="status-badge" variant="success"
                    >Active</sl-badge
                  >`}
          <div class="invite-meta">
            Expires: ${expiresDate.toLocaleDateString()}
            ${expiresDate.toLocaleTimeString()}
            ${inv.maxUses > 0
              ? ` • Used ${inv.useCount}/${inv.maxUses} times`
              : ""}
          </div>
          <div class="roles">
            ${inv.allowedRoles.map(
              (role) =>
                html`<sl-badge
                  variant="${role === "monitor" ? "primary" : "neutral"}"
                  >${role}</sl-badge
                >`,
            )}
          </div>
        </div>
        <div class="invite-actions">
          ${isActive
            ? html`
                <sl-button
                  size="small"
                  @click=${() => this.copyInviteLink(inv.inviteCode)}
                >
                  <sl-icon slot="prefix" name="clipboard"></sl-icon>
                  Copy
                </sl-button>
              `
            : ""}
          <sl-button
            size="small"
            variant="danger"
            @click=${() => this.deleteInvitation(inv.inviteCode)}
          >
            <sl-icon slot="prefix" name="trash"></sl-icon>
          </sl-button>
        </div>
      </div>
    `;
  }

  private renderMonitor(monitor: Monitor) {
    return html`
      <div class="monitor-card">
        <div class="monitor-info">
          <div class="monitor-name">${monitor.displayName}</div>
          <div class="monitor-email">${monitor.email}</div>
          <div class="monitor-type">
            <sl-badge
              variant="${monitor.relationshipType === "monitor"
                ? "primary"
                : "neutral"}"
            >
              ${monitor.relationshipType === "monitor"
                ? "Family/Friend"
                : "Healthcare Professional"}
            </sl-badge>
          </div>
        </div>
        <sl-button
          size="small"
          variant="danger"
          @click=${() => this.revokeAccess(monitor.userId)}
        >
          <sl-icon slot="prefix" name="person-x"></sl-icon>
          Revoke Access
        </sl-button>
      </div>
    `;
  }

  private renderCreateDialog() {
    return html`
      <sl-dialog
        label="Create Share Link"
        ?open=${this.showCreateDialog}
        @sl-hide=${() => {
          this.showCreateDialog = false;
          this.createdInviteUrl = "";
          this.newInviteRoles = new Set(["monitor"]);
        }}
      >
        ${this.createdInviteUrl
          ? html`
              <div class="dialog-content">
                <sl-alert variant="success" open>
                  Share link created successfully!
                </sl-alert>
                <div class="invite-url-box">
                  <div class="invite-url-label">Share this link:</div>
                  <div class="invite-url">
                    ${window.location.origin}${this.createdInviteUrl}
                  </div>
                </div>
                <sl-button
                  class="copy-button"
                  variant="primary"
                  @click=${() => this.copyFullUrl(this.createdInviteUrl)}
                >
                  <sl-icon slot="prefix" name="clipboard"></sl-icon>
                  Copy Link
                </sl-button>
              </div>
              <sl-button
                slot="footer"
                @click=${() => {
                  this.showCreateDialog = false;
                  this.createdInviteUrl = "";
                }}
              >
                Done
              </sl-button>
            `
          : html`
              <div class="dialog-content">
                <p>Choose who can use this invite link:</p>
                <div class="role-checkboxes">
                  <div class="role-option">
                    <sl-checkbox
                      ?checked=${this.newInviteRoles.has("monitor")}
                      @sl-change=${(e: Event) =>
                        this.toggleNewInviteRole(
                          "monitor",
                          (e.target as HTMLInputElement).checked,
                        )}
                    >
                      Friend/Family Monitor
                    </sl-checkbox>
                    <div class="role-description">
                      They'll receive real-time updates of your data
                    </div>
                  </div>
                  <div class="role-option">
                    <sl-checkbox
                      ?checked=${this.newInviteRoles.has("clinician")}
                      @sl-change=${(e: Event) =>
                        this.toggleNewInviteRole(
                          "clinician",
                          (e.target as HTMLInputElement).checked,
                        )}
                    >
                      Healthcare Professional
                    </sl-checkbox>
                    <div class="role-description">
                      They'll see your data when they view your profile
                    </div>
                  </div>
                </div>
              </div>
              <sl-button
                slot="footer"
                @click=${() => (this.showCreateDialog = false)}
              >
                Cancel
              </sl-button>
              <sl-button
                slot="footer"
                variant="primary"
                ?loading=${this.isCreating}
                ?disabled=${this.newInviteRoles.size === 0}
                @click=${this.createInvitation}
              >
                Create Link
              </sl-button>
            `}
      </sl-dialog>
    `;
  }

  private toggleNewInviteRole(role: string, checked: boolean) {
    const newRoles = new Set(this.newInviteRoles);
    if (checked) {
      newRoles.add(role);
    } else {
      newRoles.delete(role);
    }
    this.newInviteRoles = newRoles;
  }

  private async createInvitation() {
    if (this.newInviteRoles.size === 0) return;

    this.isCreating = true;
    try {
      const result = await relationshipService.createInvitation({
        allowedRoles: Array.from(this.newInviteRoles),
        expiryDays: 7,
        isSingleUse: true,
      });
      this.createdInviteUrl = result.inviteUrl;
      await this.loadData(); // Refresh list
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to create invitation";
    } finally {
      this.isCreating = false;
    }
  }

  private async deleteInvitation(code: string) {
    if (!confirm("Delete this invitation?")) return;

    try {
      await relationshipService.deleteInvitation(code);
      await this.loadData();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to delete invitation";
    }
  }

  private async revokeAccess(monitorUserId: string) {
    if (!confirm("Revoke this person's access to your data?")) return;

    try {
      await relationshipService.revokeMonitorAccess(monitorUserId);
      await this.loadData();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to revoke access";
    }
  }

  private copyInviteLink(code: string) {
    const url = `${window.location.origin}/invite/${code}`;
    navigator.clipboard.writeText(url);
  }

  private copyFullUrl(url: string) {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
  }

  // ============================================
  // Clinic Sharing
  // ============================================

  private renderClinicDialog() {
    // Filter to show only clinics we're not already sharing with
    const unsharedClinics = this.availableClinics.filter((c) => !c.isSharing);

    return html`
      <sl-dialog
        label="Add Clinic"
        ?open=${this.showClinicDialog}
        @sl-hide=${() => (this.showClinicDialog = false)}
      >
        <div class="dialog-content">
          <p>
            Share your data with a clinic. All clinicians at this clinic will be
            able to view your glucose data.
          </p>

          ${unsharedClinics.length === 0
            ? html`
                <div class="empty-state" style="padding: 1rem;">
                  <p>No clinics available to share with</p>
                  <small style="color: #6c757d;">
                    Ask your healthcare provider to register their clinic
                  </small>
                </div>
              `
            : html`
                <div class="monitors-list" style="margin-top: 1rem;">
                  ${unsharedClinics.map(
                    (clinic) => html`
                      <div class="monitor-card">
                        <div class="monitor-info">
                          <div class="monitor-name">${clinic.name}</div>
                          ${clinic.description
                            ? html`<div class="monitor-email">
                                ${clinic.description}
                              </div>`
                            : ""}
                        </div>
                        <sl-button
                          size="small"
                          variant="primary"
                          @click=${() => this.shareWithClinic(clinic.clinicId)}
                        >
                          <sl-icon slot="prefix" name="share"></sl-icon>
                          Share
                        </sl-button>
                      </div>
                    `,
                  )}
                </div>
              `}
        </div>
        <sl-button
          slot="footer"
          @click=${() => (this.showClinicDialog = false)}
        >
          Close
        </sl-button>
      </sl-dialog>
    `;
  }

  private async shareWithClinic(clinicId: string) {
    try {
      await relationshipService.shareWithClinic(clinicId);
      this.showClinicDialog = false;
      await this.loadData();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to share with clinic";
    }
  }

  private async revokeClinicShare(clinicId: string) {
    if (!confirm("Revoke this clinic's access to your data?")) return;

    try {
      await relationshipService.revokeClinicShare(clinicId);
      await this.loadData();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to revoke clinic access";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "share-page": SharePage;
  }
}
